import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const toggleServiceFavorite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ serviceId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const m = await import("./community.server");
    return m.toggleFavorite(context.userId, data.serviceId);
  });

export const listMyFavorites = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const m = await import("./community.server");
    return m.listFavorites(context.userId);
  });

export const createBuyerRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        title: z.string().min(5).max(140),
        description: z.string().min(20).max(4000),
        categoryId: z.string().uuid().optional(),
        budgetMinCents: z.number().int().min(500).max(10_000_000),
        budgetMaxCents: z.number().int().min(500).max(10_000_000),
        deliveryDays: z.number().int().min(1).max(180).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const m = await import("./community.server");
    return m.createRequest(context.userId, data);
  });

export const listBuyerRequests = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ categoryId: z.string().uuid().optional(), mine: z.boolean().optional() }).parse(d ?? {}))
  .handler(async ({ data, context }) => {
    const m = await import("./community.server");
    return m.listRequests({ categoryId: data.categoryId, mine: data.mine ? context.userId : undefined });
  });

export const getBuyerRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ requestId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const m = await import("./community.server");
    return m.getRequest(context.userId, data.requestId);
  });

export const sendProposal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        requestId: z.string().uuid(),
        message: z.string().min(20).max(3000),
        priceCents: z.number().int().min(500).max(10_000_000),
        deliveryDays: z.number().int().min(1).max(180),
        serviceId: z.string().uuid().optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const m = await import("./community.server");
    return m.createProposal(context.userId, data);
  });

export const decideProposalFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ proposalId: z.string().uuid(), decision: z.enum(["accepted", "declined"]) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const m = await import("./community.server");
    return m.decideProposal(context.userId, data.proposalId, data.decision);
  });

export const sendCustomOffer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        buyerUserId: z.string().uuid(),
        title: z.string().min(3).max(140),
        description: z.string().max(3000).optional(),
        priceCents: z.number().int().min(500).max(10_000_000),
        deliveryDays: z.number().int().min(1).max(180),
        revisions: z.number().int().min(0).max(10),
        serviceId: z.string().uuid().optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const m = await import("./community.server");
    return m.createCustomOffer(context.userId, data);
  });

export const listMyCustomOffers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const m = await import("./community.server");
    return m.listCustomOffers(context.userId);
  });

export const acceptCustomOfferFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ offerId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const m = await import("./community.server");
    return m.acceptCustomOffer(context.userId, data.offerId);
  });

export const declineCustomOfferFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ offerId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const m = await import("./community.server");
    return m.declineCustomOffer(context.userId, data.offerId);
  });
